import { filterDOMProps } from '@react-aria/utils';
import clsx from 'clsx';
import * as React from 'react';
import { HeadingContext, Provider } from 'react-aria-components';

import { DescriptionContext } from '../Description';
import { type LabelPropsInput, useLabelProps } from '../util/labeling';
import { CardDismissButton } from './CardDismissButton';

const cardDOMPropNames = new Set([
    'className',
    'role',
    'slot',
    'style',
    'tabIndex',
    // Set by overlay triggers (`DialogTrigger`, …) to expose the popup a card opens.
    'aria-haspopup',
    'aria-expanded',
    'aria-controls',
]);
type FilterCardDOMPropsOptions = {
    isLink?: boolean;
};

export type GenericCardHookProps = {
    className?: string;
    onDismiss?: () => void;
    kind?: 'default' | 'glass' | 'flat' | 'ghost';
};

export type CardHookProps = GenericCardHookProps & LabelPropsInput;

export type CardResult = {
    className: string;
    'aria-labelledby'?: string | undefined;
    'aria-describedby'?: string | undefined;
    'aria-label'?: string | undefined;
};

export type CardProps = React.ComponentPropsWithoutRef<'div'> & GenericCardHookProps;

/**
 * Filter the DOM props supported by card wrappers before forwarding them to the DOM.
 */
export function filterCardDOMProps(
    props: React.ComponentPropsWithoutRef<'div'> | React.ComponentPropsWithoutRef<'a'>,
    options?: FilterCardDOMPropsOptions
) {
    return filterDOMProps(props, {
        events: true,
        global: true,
        isLink: options?.isLink,
        labelable: true,
        propNames: cardDOMPropNames,
    });
}

/**
 * Card component to wrap content in a card.
 */
export function Card({ ref, ...props }: CardProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const headingId = null;
    const descriptionId = null;
    const { onDismiss, children, ...rest } = props;
    const cardProps = useCard({ ...props, headingId, descriptionId });
    const domProps = filterCardDOMProps(rest);

    return (
        <CardProvider headingId={headingId} descriptionId={descriptionId}>
            <div ref={ref} {...domProps} {...cardProps}>
                {onDismiss ? <CardDismissButton onDismiss={onDismiss} /> : null}
                {children}
            </div>
        </CardProvider>
    );
}

export function CardProvider(props: {
    headingId: string | null;
    descriptionId: string | null;
    children: React.ReactNode;
}) {
    return (
        <Provider
            values={[
                [
                    HeadingContext,
                    {
                        level: 2,
                        className: 'text-ui-heading-medium',
                        id: props.headingId ?? undefined,
                    },
                ],
                [
                    DescriptionContext,
                    {
                        id: props.descriptionId ?? undefined,
                    },
                ],
            ]}
        >
            {props.children}
        </Provider>
    );
}

/**
 * Get the card class name, used to create components that mimic a card.
 */
export function getCardClassName(props?: Pick<GenericCardHookProps, 'kind'>) {
    return clsx(
        'group/card relative transition-[border-color,background-color]',
        {
            // No bg-* class here: the background is transparent by default, and an explicit
            // bg-transparent would defeat consumer `className` overrides (CSS order, not
            // class order, decides conflicting Tailwind utilities).
            default: 'rounded-lg border border-neutral-subtle shadow-subtle',
            glass: 'rounded-xl border border-neutral-subtle shadow-subtle [background:var(--sp-color-bg-card-glass-default)]',
            flat: 'rounded-lg',
            ghost: '',
        }[props?.kind ?? 'default']
    );
}

/**
 * Low-level hook to create a card component.
 */
export function useCard(props: CardHookProps): CardResult {
    return {
        className: clsx(getCardClassName(props), props.className),
        ...useLabelProps(props),
    };
}
